/* The e-mail renderer — one shell, two header states (brand book §08).
   Source of truth: packages/brand/email/shell.html (imported verbatim).
   GIFT class keeps the Ink-dark header with the spark; every other class
   gets a Paper header, no spark, no gift block. Footers vary by class:
   the game frame and the gift assurance never appear on a review ask or
   an account e-mail (guardrail — never a prize mention on a review ask). */
import shellRaw from '@brand/email/shell.html?raw'

export type EmailClass = 'transactionnel' | 'rappel' | 'avis' | 'marketing' | 'compte' | 'marchand'

export interface EmailTemplate {
  id: string
  name: string // template-library name
  classe: EmailClass
  classeLabel: string
  subject: string
  preheader: string
  headline: string
  body: string // may contain <b> — inline HTML allowed
  gift?: { prize: string; code: string; location: string; expiry: string }
  cta?: { label: string; url: string }
  from: string
  sendRule: string // when it fires + the rule that governs it
  copyStatus: 'verbatim' | 'draft' // verbatim = drawn in a wireframe frame; draft = to validate at the FR proofread
  frameRef: string
}

const GIFT_BLOCK_START = '<!-- the gift'
const FOOTER_GAME = "Jeu gratuit sans obligation d’achat"
const FOOTER_ASSURE = 'Le cadeau reste disponible, avec ou sans avis.'

function fill(tpl: string, vars: Record<string, string>) {
  return tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? '')
}

/** Render a template to a complete, inbox-ready HTML document. */
export function renderEmail(t: EmailTemplate): string {
  let html = shellRaw

  const isGift = t.gift !== undefined

  if (!isGift) {
    /* Paper header, no spark: swap the Ink header for Paper + dark text,
       and remove the gift block (label, prize, code, caisse line). */
    html = html
      .replace(/bgcolor="#20261F" style="background:#20261F;/, 'bgcolor="#FAF8F2" style="background:#FAF8F2;border-bottom:1px solid #E4E0D5;')
      .replace(/font-weight:bold;color:#FAF8F2;letter-spacing:-0.2px;/, 'font-weight:bold;color:#20261F;letter-spacing:-0.2px;')
      .replace(/font-size:28px;line-height:1.25;color:#FAF8F2;/, 'font-size:28px;line-height:1.25;color:#20261F;')
      .replace(/font-size:16px;line-height:1.6;color:#B4BEB4;/, 'font-size:16px;line-height:1.6;color:#4C544B;')
    /* cut the gift block: from its marker comment to the end of the caisse line */
    const start = html.indexOf(GIFT_BLOCK_START)
    const endMarker = 'Valable jusqu\'au {{EXPIRY}}.</p>'
    const end = html.indexOf(endMarker)
    if (start > -1 && end > -1) html = html.slice(0, start) + html.slice(end + endMarker.length)
    /* and the gift-only conditions pair in the body */
    html = html.replace('<p style="margin:0 0 8px 0;">{{CONDITIONS_LINE}}</p>', '')
    html = html.replace('<p style="margin:0;font-size:14px;color:#6B7268;">Ni échangeable ni remboursable en espèces.</p>', '')
  }

  /* footer by class */
  if (t.classe === 'avis' || t.classe === 'marketing') {
    /* review ask & marketing: no game frame, no gift assurance (guardrail) */
    html = html.replace(new RegExp(`<p style="margin:0 0 6px 0;">${FOOTER_GAME}[^<]*<a[^>]*>règlement</a></p>`), '')
    html = html.replace(`<p style="margin:0 0 6px 0;">${FOOTER_ASSURE}</p>`, '')
  }
  if (t.classe === 'compte' || t.classe === 'marchand') {
    /* account & merchant mail: minimal footer — no game frame, no assurance, no unsubscribe */
    html = html.replace(new RegExp(`<p style="margin:0 0 6px 0;">${FOOTER_GAME}[^<]*<a[^>]*>règlement</a></p>`), '')
    html = html.replace(`<p style="margin:0 0 6px 0;">${FOOTER_ASSURE}</p>`, '')
    html = html.replace(/ · <a href="\{\{UNSUB_URL\}\}"[^<]*<\/a>/, '')
  }
  if (t.classe !== 'transactionnel' && t.classe !== 'rappel') {
    /* the « Voir tous mes cadeaux » link belongs only to gift & reminder mail —
       on a review ask it would be a prize mention (guardrail); on account,
       marketing and merchant mail it is simply out of place */
    html = html.replace(/<p style="margin:20px 0 0 0;font-size:14px;line-height:1.6;">[\s\S]*?<\/p>/, '')
  }
  if (!t.cta) {
    /* no CTA (e.g. the sign-in code) — remove the button table and gifts link */
    html = html.replace(/<table role="presentation"[^>]*style="margin:26px 0 0 0;">[\s\S]*?<\/table>/, '')
    html = html.replace(/<p style="margin:20px 0 0 0;font-size:14px;line-height:1.6;">[\s\S]*?<\/p>/, '')
  }

  return fill(html, {
    SUBJECT: t.subject,
    PREHEADER: t.preheader,
    HEADLINE: t.headline,
    BODY: t.body,
    PRIZE_NAME: t.gift?.prize ?? '',
    CODE: t.gift?.code ?? '',
    LOCATION_NAME: t.gift?.location ?? 'Valessio — Paris 9',
    EXPIRY: t.gift?.expiry ?? '',
    CONDITIONS_LINE: isGift ? 'Utilisable en une fois, dès votre prochaine visite. Conditions du cadeau (v. 12) — figées à l’émission.' : '',
    CTA_URL: t.cta?.url ?? '#',
    CTA_LABEL: t.cta?.label ?? '',
    GIFTS_URL: '#',
    RULES_URL: '#',
    UNSUB_URL: '#',
    ORGANISER: 'Valessio SARL',
    ASSET_BASE: '',
  })
}
